import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

export default function Contact() {
  const { setNeutralTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setNeutralTheme();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Message sent successfully! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 bg-zinc-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-zinc-900 mb-6" data-testid="contact-title">
            Get in Touch
          </h1>
          <p className="text-xl text-zinc-600 leading-relaxed">
            Have questions about Zenvy? Want to partner with us? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-zinc-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-zinc-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-1">Office Address</h3>
                    <p className="text-zinc-600">123 Business Avenue, Suite 100<br />New York, NY 10001</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-zinc-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-1">Phone</h3>
                    <p className="text-zinc-600">+1 (555) 123-4567</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-zinc-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-1">Email</h3>
                    <p className="text-zinc-600">hello@zenvy.com</p>
                    <p className="text-zinc-600">support@zenvy.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-zinc-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-1">Business Hours</h3>
                    <p className="text-zinc-600">Monday - Friday: 9:00 AM - 6:00 PM</p>
                    <p className="text-zinc-600">Saturday: 10:00 AM - 4:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-zinc-200">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-zinc-900 mb-6">Send us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Doe"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="john@example.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder="How can we help?"
                      required
                      data-testid="contact-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us more about your inquiry..."
                      rows={5}
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full py-6 bg-zinc-900 hover:bg-zinc-800"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        Send Message <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-zinc-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-zinc-900">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              { q: 'How do I book an appointment?', a: 'Simply browse salons, select a service, choose your preferred date and time, and confirm your booking.' },
              { q: 'Can I cancel or reschedule my booking?', a: 'Yes, you can manage your bookings through your dashboard. Please note that cancellation policies may vary by salon.' },
              { q: 'How do I become a partner salon?', a: 'Register as a shop owner and create your salon profile. Our team will review and verify your listing.' },
              { q: 'Is there a fee for customers?', a: 'No, Zenvy is completely free for customers. You only pay for the services at the salon.' }
            ].map((faq, i) => (
              <Card key={i} className="border-zinc-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-zinc-900 mb-2">{faq.q}</h3>
                  <p className="text-zinc-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
